import React, { useEffect, useState } from 'react';
import { Button } from '@zurich-primitives/button';
import { GeneratingExtraction } from 'app/components/Modals';
import { SummaryField } from 'app/components/SummaryField';
import {
  getBusinessArea,
  getCurrentFile,
  getCurrentSummary,
  getRegenerateExtraction,
  getService,
  getSummaryEdited,
  setCurrentFile,
  setCurrentSummary,
  setGeneratingExtraction,
  setHash,
  setRegenerateExtraction,
  setSummaryEdited,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import {
  IExtractionResult,
  IPutData,
  resetFieldsValues,
  ServicesData
} from 'app/utils';
import {
  generateCSV,
  setFieldsValues,
  transformText
} from 'app/utils/functions';
import config from 'config';
import { getFileExtraction, updateExtraction } from 'integration';
import { useNavigate } from 'react-router-dom';
import styles from '../Summary/Summary.module.scss';
import { Link } from '@zurich-data-dsm-icons/action.link';
import {
  ContractSummarizerTypeSelectorTooltip,
  SERVICE_NAMES
} from 'app/utils/consts';
import { DownloadCSVButton } from 'app/components';

export const ContractSummarizer: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentFile = useAppSelector(getCurrentFile);
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const [fields, setFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [originalExtraction, setOriginalExtraction] =
    useState<IExtractionResult>();
  const types = ['legal_summary', 'legal_summary_2'];
  const [selectedType, setSelectedType] = useState<string>('legal_summary');
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const isSummaryEdited = useAppSelector(getSummaryEdited);
  const currentSummary = useAppSelector(getCurrentSummary);
  const regenerateExtraction = useAppSelector(getRegenerateExtraction);
  const AVG_DURATION_IN_MS = 30000;

  const onFinally = (): void => {
    dispatch(setRegenerateExtraction(false));
    dispatch(setGeneratingExtraction(false));
  };

  const handleOpenAPI = (): void => {
    currentService !== undefined &&
      navigate(config.APP.PATH.API.replace(':api', currentService));
  };

  const handleSaveSummary = (): void => {
    currentSummary &&
      updateExtraction(
        String(currentBusinessArea),
        String(currentService),
        currentSummary as IPutData
      );
    dispatch(setSummaryEdited(false));
  };

  const callGetFileExtraction = (body: any): void => {
    getFileExtraction(String(currentBusinessArea), String(currentService), body)
      .then((response) => {
        const currentSummary: IPutData = {
          hash: response.hash,
          data: [],
          type: body.type
        };

        const fields = [];
        for (const field in response.results) {
          const value = response.results[field].value.trim();

          fields.push({
            query: field,
            value
          });
          currentSummary.data.push({
            key: field,
            value,
            updated: false
          });
        }

        dispatch(setCurrentSummary(currentSummary));
        setFields(fields);
        setOriginalExtraction(response.results);
        dispatch(setHash(response.hash));
      })
      .catch(() => {
        resetFieldsValues();
        dispatch(setCurrentFile(undefined));
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        onFinally();
      });
  };

  const fetchContractSummarizer = ({
    file,
    type,
    ignoreCache,
    uploadToStorage
  }: {
    file?: File;
    type?: string;
    ignoreCache?: boolean;
    uploadToStorage?: boolean;
  }): void => {
    dispatch(setGeneratingExtraction(true));
    const body = {
      file,
      type,
      ...(ignoreCache && { ignoreCache }),
      uploadToStorage
    };
    setCurrentSummary(undefined);
    callGetFileExtraction(body);
  };

  const handleOnTypeSelectorChange = (e: { target: { value: any } }): void => {
    setSelectedType(e.target.value);

    dispatch(setGeneratingExtraction(true));

    const body = {
      file: currentFile,
      type: e.target.value,
      uploadToStorage: false
    };
    setCurrentSummary(undefined);
    callGetFileExtraction(body);
  };

  const handleDownloadCSV = (): void => {
    if (currentSummary) {
      generateCSV(currentSummary.data as [], currentFile?.name || '');
    }
  };

  useEffect(() => {
    if (
      currentFile &&
      currentService === SERVICE_NAMES.CONTRACT_SUMMARIZER &&
      (regenerateExtraction || regenerateExtraction === null)
    ) {
      fetchContractSummarizer({
        file: currentFile,
        type: selectedType,
        ignoreCache:
          regenerateExtraction === null ? false : regenerateExtraction,
        uploadToStorage: false
      });
    }
    if (!currentFile) resetFieldsValues();
  }, [currentFile, regenerateExtraction]);

  useEffect(() => {
    if (originalExtraction) {
      setFieldsValues(originalExtraction);
    } else {
      resetFieldsValues();
    }
  }, [originalExtraction]);

  useEffect(() => {
    dispatch(setRegenerateExtraction(null));
  }, [currentBusinessArea, currentService]);

  return (
    <div className={styles['summary-section']}>
      <div className={styles.header}>
        <div className={styles['hihglight-switch']}>
          <h3>Contract Summarizer</h3>
          <select
            id="legalSelect"
            defaultValue={selectedType}
            className={styles['select-element']}
            onChange={handleOnTypeSelectorChange}
            data-tooltip-id="type-selector-tooltip"
            data-tooltip-content={ContractSummarizerTypeSelectorTooltip}
          >
            {types.map((type) => (
              <option key={type} value={type}>
                {transformText(type)}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={handleOpenAPI} variant="tertiary">
          <Button.Icon>
            <Link />
          </Button.Icon>
          <Button.Label>Use our API version</Button.Label>
        </Button>
      </div>
      {fields.map((field) => (
        <SummaryField
          key={`field-${field.query}`}
          id={`field-${field.query}`}
          title={field.query}
          currentTitle={currentTitle}
          setCurrentTitle={setCurrentTitle}
          selectedType={selectedType}
        />
      ))}
      <div className={styles.buttons}>
        {isSummaryEdited && (
          <Button
            className={styles['btn-csv']}
            onClick={handleSaveSummary}
            variant="secondary"
          >
            <Button.Label>Save changes</Button.Label>
          </Button>
        )}
        {currentService !== undefined &&
          ServicesData[currentService].ExtractionType === 'extraction' && (
            <DownloadCSVButton
              handleOnClick={handleDownloadCSV}
              isDisabled={originalExtraction === undefined}
            />
          )}
      </div>
      <GeneratingExtraction processTime={AVG_DURATION_IN_MS} />
    </div>
  );
};
